import { IAppProps, SCREEN_NAME } from '../interface/IAppProvider';
import { getDefaultOptions, HTTP_METHOD } from './GetAsyncOptions';
import {
    IPackageDetails,
    IBuyInsurance,
    EMAIL_EVENT,
    IGetPaymentAgreement,
    IPutStakeholderInfo,
    IGetCustomerContentStatus
} from '../interface/IPayload';
import { TDispatch } from '../context/AppContext';

// Handle HTTP related errors
// This function will return a Promise or undefined. That makes it CRAZY difficult follow to execution flow and
// reason about how calling code will respond. In need of refactor.
function handleHTTPErrors(response: Response, props: IAppProps, dispatch: TDispatch, options?: RequestInit) {
    const x_amzn_requestid = response.headers.get('x-amzn-requestid');
    if (response.ok) {
        dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: false });
        return response
            .clone()
            .json()
            .catch(() => response.text());
    } else {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            response
                .clone()
                .json()
                .catch(() => response.text())
                .then((jsonResponse) => {
                    if (jsonResponse.errorCode === 20 && jsonResponse.message) {
                        props.c9log.error(
                            `handleHTTPErrors: Error Payload from server -', ${JSON.stringify(
                                jsonResponse
                            )} ${x_amzn_requestid}`
                        );
                        dispatch({
                            type: SCREEN_NAME.SET_EMAIL_EVENT,
                            payload: EMAIL_EVENT.GENERAL_ERROR
                        });
                        dispatch({ type: SCREEN_NAME.PRICE_ZERO, payload: true });
                        dispatch({ type: SCREEN_NAME.SET_STATE_CHANGE, payload: true });
                    } else if (
                        response.status === 400 &&
                        jsonResponse.message === 'Invalid Customer identified - Customer Marking'
                    ) {
                        dispatch({ type: SCREEN_NAME.SET_CUSTOMER_HAS_MARKING, payload: true });
                        // this just to avoid calling the routeToErrorPage
                        return undefined;
                    } else {
                        props.c9log.error(
                            `handleHTTPErrors: Called ${response.url} Response: ${response.status} ${
                                response.statusText
                            } ${response.type} ${JSON.stringify(options)} ${x_amzn_requestid} unknown error happened`,
                            'AsyncCalls'
                        );
                    }

                    routeToErrorPage(props, dispatch);
                });
        } else if (contentType && contentType.indexOf('text/plain') !== -1) {
            return response
                .clone()
                .json()
                .catch(() => response.text())
                .then((jsonResponse) => {
                    return jsonResponse;
                });
        } else {
            props.c9log.error(
                `handleHTTPErrors: Called ${response.url} Response: ${response.status} ${response.statusText} ${
                    response.type
                } ${JSON.stringify(options)} ${x_amzn_requestid} contentType was not correct`,
                'AsyncCalls'
            );
            dispatch({
                type: SCREEN_NAME.SET_EMAIL_EVENT,
                payload: EMAIL_EVENT.GENERAL_ERROR
            });
            dispatch({ type: SCREEN_NAME.PRICE_ZERO, payload: true });
            dispatch({ type: SCREEN_NAME.SET_STATE_CHANGE, payload: true });
        }
    }
}

function handleGeneralErrors(serviceEndpoint: string, error: any, props: IAppProps, dispatch: TDispatch) {
    props.c9log.error(`handleGeneralErrors: Called ${serviceEndpoint} - error: ${error}`, 'AsyncCalls');
    routeToErrorPage(props, dispatch);
    throw error;
}

// Goto error url
export function routeToErrorPage(_props: IAppProps, dispatch: TDispatch) {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: false });
    dispatch({ type: SCREEN_NAME.SET_ERROR, payload: true });
}

// Quote GET request Query Param Generator
const getQuotesParamGenerator = (param: IPackageDetails): string => {
    if (param && param.packages) {
        const quoteIDList = param.packages.map((item) => item.quoteId);
        return `quoteid=${quoteIDList && quoteIDList.join('&quoteid=')}`;
    } else {
        return '';
    }
};

// Get All the resource bundle by section
export const getContentText = (props: IAppProps, dispatch: TDispatch, section: string) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    return fetch(`${props.config.resourceBundleApiUrl}/${section}`)
        .then((response) => {
            if (response.ok) {
                return response.json().then((json) => {
                    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: false });
                    return { name: section, data: json };
                });
            } else {
                dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: false });
                throw new Error(`Could not call ${response.url}`);
            }
        })
        .catch((err) => {
            props.c9log.error(`getContentText: ${props.config.resourceBundleApiUrl}/${section} - ${err}`, 'AsyncCalls');
            routeToErrorPage(props, dispatch);
        });
};

// Create package Service
export const createPackage = (props: IAppProps, dispatch: TDispatch, body: object): Promise<Response> => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.POST, props, 'wso2', body);
    const loggedOn = window.topContext.user.isLoggedOn;
    const baseUrl = loggedOn ? props.config.wso2ApiUrl : props.config.wso2OpenApiUrl;
    return fetch(`${baseUrl}/packages`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.wso2OpenApiUrl}/packages`, err, props, dispatch);
        }
    );
};

// Update package API
export const updatePackage = (props: IAppProps, dispatch: TDispatch, body: any): Promise<Response> => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.PUT, props, 'wso2', body);
    const loggedOn = window.topContext.user.isLoggedOn;
    const baseUrl = loggedOn ? props.config.wso2ApiUrl : props.config.wso2OpenApiUrl;
    return fetch(`${baseUrl}/packages`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.wso2OpenApiUrl}/packages`, err, props, dispatch);
        }
    );
};

/**
 * @deprecated - use static list from /assets/companies.json
 */
export const getCompanies = (props: IAppProps, dispatch: TDispatch): Promise<any> => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'tilbudsflow');
    const URL = `${props.config.insuranceCompaniesApiUrl}`;
    return fetch(URL, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(URL, err, props, dispatch);
        }
    );
};

// Get quotes for Packages Screen
export const getQuotes = (props: IAppProps, dispatch: TDispatch, requestParam: IPackageDetails) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2');
    const quoteIDParamString = getQuotesParamGenerator(requestParam); // ?quoteid=..........
    const query = `brand=${requestParam.brand}&productkey=${requestParam.productKey}&${quoteIDParamString}`;
    const loggedOn = window.topContext.user.isLoggedOn;
    const baseUrl = loggedOn ? props.config.wso2ApiUrl : props.config.wso2OpenApiUrl;
    return fetch(`${baseUrl}/packages?${query}`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.wso2OpenApiUrl}/packages?${query}`, err, props, dispatch);
        }
    );
};

//Send email to Customer service
export const sendEmail = (props: IAppProps, dispatch: TDispatch, body: object) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.POST, props, 'wso2', body);
    return fetch(`${props.config.wso2OpenApiUrl}/email`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.wso2OpenApiUrl}/email`, err, props, dispatch);
        }
    );
};

// Persist data to cache and return key string
export const sendDataToObjectCache = async (
    props: IAppProps,
    dispatch: TDispatch,
    data: any
): Promise<{ objectId: string }> => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.POST, props, 'objectCache', data);
    return fetch(`${props.config.objectCacheUrl}`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(props.config.objectCacheUrl, err, props, dispatch);
        }
    );
};

// Get data from object cache
export const getDataToObjectCache = async (props: IAppProps, dispatch: TDispatch, cacheId: string) => {
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'objectCache');
    return fetch(`${props.config.objectCacheUrl}/${cacheId}`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.objectCacheUrl}/${cacheId}`, err, props, dispatch);
        }
    );
};

// Buy Insurance
export const buyInsurance = (props: IAppProps, dispatch: TDispatch, data: IBuyInsurance) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.PUT, props, 'wso2Secured', data);
    return fetch(`${props.config.wso2ApiUrl}/packages/buy`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.wso2ApiUrl}/packages/buy`, err, props, dispatch);
        }
    );
};

// Get info about customer
export const getStakeHolderInfo = (props: IAppProps, dispatch: TDispatch) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    return fetch(`${props.config.gtiServiceApiUrl}/api/stakeholder`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.gtiServiceApiUrl}/api/stakeholder`, err, props, dispatch);
        }
    );
};

/**
 *  For logged in users, dont pass the CPR param. For late login pass the CPR.
 *  If no JWT, call with CPR. If a jwt is present, do not pass the CPR param.
 * @param props
 * @param dispatch
 * @param kvhx
 * @param cprNr
 * @returns
 */
export const getCustomerContentStatus = (
    props: IAppProps,
    dispatch: TDispatch,
    kvhx: string,
    cprNr?: string
): Promise<IGetCustomerContentStatus> => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    const baseUrl = !cprNr ? props.config.wso2ApiUrl : props.config.wso2OpenApiUrl;
    const url = `${baseUrl}/insurance/content/status?kvhx=${kvhx}&cprNr=${cprNr || ''}`;

    return fetch(url, options).then(
        (response) => {
            return handleHTTPErrors(response, props, dispatch, options);
        },
        (err) => {
            handleGeneralErrors(url, err, props, dispatch);
        }
    );
};

// Get household size
export const getCustomerHouseholdSize = (props: IAppProps, dispatch: TDispatch) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    return fetch(`${props.config.wso2ApiUrl}/customer/household`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.wso2ApiUrl}/customer/household`, err, props, dispatch);
        }
    );
};

// Check existing PBS contract
export const getPaymentAgreement = (props: IAppProps, dispatch: TDispatch, requestParam: IGetPaymentAgreement) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    const url = `${props.config.wso2ApiUrl}/payments/pbs?quoteId=${requestParam.quoteId}&productId=${requestParam.productId}`;
    return fetch(url, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(url, err, props, dispatch);
        }
    );
};

//Update customer info in GTI
export const putCustomerInfo = (
    props: IAppProps,
    dispatch: TDispatch,
    customerId: string,
    stakeholder: IPutStakeholderInfo
) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.POST, props, 'gtiUpdate', stakeholder);
    const url = `${props.config.updateUserInfoApiUrl}/${customerId}`;
    return fetch(url, options).then((response) => handleHTTPErrors(response, props, dispatch, options));
};

export const getEboksSubscription = (props: IAppProps, dispatch: TDispatch, customerId: string) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    return fetch(`${props.config.gteboksServiceUrl}/status?brand=TD&id=${customerId}`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(
                `${props.config.gteboksServiceUrl}/status?brand=TD&id=${customerId}`,
                err,
                props,
                dispatch
            );
        }
    );
};

export const doEboksSubscription = (props: IAppProps, dispatch: TDispatch, customerId: string) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    return fetch(`${props.config.gteboksServiceUrl}/subscribe?brand=TD&id=${customerId}`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(
                `${props.config.gteboksServiceUrl}/subscribe?brand=TD&id=${customerId}`,
                err,
                props,
                dispatch
            );
        }
    );
};

export const fetchGiftCardDetails = (props: IAppProps, dispatch: TDispatch) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    return fetch(`${props.config.giftCardServiceUrl}/?key=content`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.giftCardServiceUrl}/?key=content`, err, props, dispatch);
        }
    );
};

export const getCustomerMarking = (props: IAppProps, dispatch: TDispatch) => {
    dispatch({ type: SCREEN_NAME.LOADING_DATA, payload: true });
    const options = getDefaultOptions(HTTP_METHOD.GET, props, 'wso2Secured');
    return fetch(`${props.config.customerMarkingServiceUrl}?product=content`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(`${props.config.customerMarkingServiceUrl}/?product=content`, err, props, dispatch);
        }
    );
};

type BankInfo = {
    regNumber: string;
    accNumber: string;
};
export const checkBankInfo = async (props: IAppProps, dispatch: TDispatch, bankInfo: BankInfo) => {
    const options = getDefaultOptions(HTTP_METHOD.POST, props, 'wso2Secured', {
        regNo: bankInfo.regNumber,
        accountNo: bankInfo.accNumber
    });

    return fetch(`${props.config.bankValidationUrl}/validator`, options).then(
        (response) => handleHTTPErrors(response, props, dispatch, options),
        (err) => {
            handleGeneralErrors(
                `${props.config.wso2ApiHost}/proxy/v2/openapi/automated-payment-service/payment/services/bankaccount/validator`,
                err,
                props,
                dispatch
            );
        }
    );
};
